import React, { useState, useEffect } from 'react';
import { get2FAStatus, setup2FA, enable2FA, disable2FA, regenerate2FABackupCodes } from '../api';

// Self-contained 2FA (TOTP) management card, rendered inside the Profile page.
// Flow: view status → enroll (QR + confirm code → backup codes) or disable.
export default function TwoFactorSettings() {
  const [status, setStatus] = useState(null); // { enabled, backup_codes_remaining }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Enrollment state
  const [enroll, setEnroll] = useState(null); // { qr_data_url, secret }
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null); // shown once

  // Disable / regenerate state
  const [showDisable, setShowDisable] = useState(false);
  const [password, setPassword] = useState('');
  const [regenCode, setRegenCode] = useState('');
  const [showRegen, setShowRegen] = useState(false);

  const refresh = async () => {
    try { setStatus(await get2FAStatus()); } catch { setStatus({ enabled: false }); }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setError(''); setBusy(true);
    try { setEnroll(await setup2FA()); }
    catch (err) { setError(err.response?.data?.error || 'Could not start setup'); }
    setBusy(false);
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const res = await enable2FA(code.trim());
      setBackupCodes(res.backup_codes);
      setEnroll(null); setCode('');
      await refresh();
    } catch (err) { setError(err.response?.data?.error || 'Could not enable 2FA'); }
    setBusy(false);
  };

  const confirmDisable = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await disable2FA(password);
      setShowDisable(false); setPassword(''); setBackupCodes(null);
      await refresh();
    } catch (err) { setError(err.response?.data?.error || 'Could not disable 2FA'); }
    setBusy(false);
  };

  const confirmRegen = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const res = await regenerate2FABackupCodes(regenCode.trim());
      setBackupCodes(res.backup_codes);
      setShowRegen(false); setRegenCode('');
      await refresh();
    } catch (err) { setError(err.response?.data?.error || 'Could not regenerate codes'); }
    setBusy(false);
  };

  const cancelEnroll = () => { setEnroll(null); setCode(''); setError(''); };

  const errBox = error ? (
    <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: '0.875rem', background: '#fef2f2', color: '#dc2626' }}>{error}</div>
  ) : null;

  const codesPanel = backupCodes ? (
    <div style={{ marginTop: 16, padding: 16, borderRadius: 10, background: 'var(--glass-1, #f8fafc)', border: '1px solid var(--hairline, #e2e8f0)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>Save your backup codes</div>
      <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 12 }}>
        Each code works once. Store them somewhere safe — they're the only way in if you lose your authenticator. This is the only time they're shown.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontFamily: 'monospace', fontSize: '0.95rem' }}>
        {backupCodes.map(c => <div key={c} style={{ padding: '4px 8px', background: 'var(--glass-2, #fff)', borderRadius: 6, textAlign: 'center' }}>{c}</div>)}
      </div>
      <button className="btn" style={{ marginTop: 12 }} onClick={() => navigator.clipboard?.writeText(backupCodes.join('\n'))}>Copy codes</button>
      <button className="btn btn-primary" style={{ marginTop: 12, marginLeft: 8 }} onClick={() => setBackupCodes(null)}>Done</button>
    </div>
  ) : null;

  if (loading) {
    return <div className="card" style={{ marginTop: 24 }}><div style={{ color: 'var(--muted)' }}>Loading security settings…</div></div>;
  }

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text, #1e1b4b)', marginBottom: 6 }}>Two-Factor Authentication</div>
      <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 20 }}>
        Add a second step at sign-in using an authenticator app (Google Authenticator, Authy, 1Password…).
      </div>

      {errBox}

      {/* ── Enabled state ─────────────────────────────────────────────── */}
      {status?.enabled && !enroll && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontSize: '0.8rem', fontWeight: 700 }}>● On</span>
            <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{status.backup_codes_remaining} backup code{status.backup_codes_remaining === 1 ? '' : 's'} remaining</span>
          </div>
          {codesPanel}
          {!backupCodes && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn" onClick={() => { setShowRegen(true); setShowDisable(false); }}>Regenerate backup codes</button>
              <button className="btn" style={{ color: '#dc2626', borderColor: '#dc2626' }} onClick={() => { setShowDisable(true); setShowRegen(false); }}>Disable 2FA</button>
            </div>
          )}

          {showRegen && (
            <form onSubmit={confirmRegen} style={{ marginTop: 14 }}>
              <div className="form-group">
                <label>Enter a current authenticator code to confirm</label>
                <input value={regenCode} onChange={e => setRegenCode(e.target.value)} placeholder="123456" autoFocus required />
              </div>
              <button className="btn btn-primary" disabled={busy} type="submit">{busy ? 'Working…' : 'Generate new codes'}</button>
              <button className="btn" type="button" style={{ marginLeft: 8 }} onClick={() => setShowRegen(false)}>Cancel</button>
            </form>
          )}

          {showDisable && (
            <form onSubmit={confirmDisable} style={{ marginTop: 14 }}>
              <div className="form-group">
                <label>Enter your password to disable 2FA</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus required />
              </div>
              <button className="btn btn-primary" disabled={busy} type="submit" style={{ background: '#dc2626' }}>{busy ? 'Working…' : 'Disable 2FA'}</button>
              <button className="btn" type="button" style={{ marginLeft: 8 }} onClick={() => setShowDisable(false)}>Cancel</button>
            </form>
          )}
        </div>
      )}

      {/* ── Disabled state, not yet enrolling ─────────────────────────── */}
      {!status?.enabled && !enroll && !backupCodes && (
        <button className="btn btn-primary" onClick={startSetup} disabled={busy}>{busy ? 'Please wait…' : 'Enable 2FA'}</button>
      )}
      {!status?.enabled && codesPanel}

      {/* ── Enrollment (QR + confirm code) ────────────────────────────── */}
      {enroll && (
        <div>
          <div style={{ fontSize: '0.9rem', color: 'var(--text)', marginBottom: 12 }}>1. Scan this QR code with your authenticator app:</div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <img src={enroll.qr_data_url} alt="2FA QR code" style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: 16, textAlign: 'center' }}>
            Can't scan? Enter this key manually:<br />
            <code style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{enroll.secret}</code>
          </div>
          <form onSubmit={confirmEnable}>
            <div className="form-group">
              <label>2. Enter the 6-digit code to confirm</label>
              <input value={code} onChange={e => setCode(e.target.value)} placeholder="123456" autoFocus required style={{ letterSpacing: '0.2em', textAlign: 'center' }} />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Verifying…' : 'Verify & Enable'}</button>
            <button className="btn" type="button" style={{ marginLeft: 8 }} onClick={cancelEnroll}>Cancel</button>
          </form>
        </div>
      )}
    </div>
  );
}
